/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "antd";
import {
  FiHome,
  FiInfo,
  FiLogIn,
  FiLogOut,
  FiMenu,
  FiUser,
  FiX,
} from "react-icons/fi";
import { FaUserCircle } from "react-icons/fa";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";

import {
  Container,
  ContentModalGoogle,
  DesktopDropdown,
  DrawerHeader,
  LogoButton,
  MenuDivider,
  MenuItem,
  MenuToggle,
  MenuUserBlock,
  MenuWrap,
  MobileDrawer,
  MobileOverlay,
  Nav,
  Spacer,
  UserRow,
} from "./styles";
import { useAuth } from "../../context/auth";
import { LoadSpinnerLogin } from "../LoadSpinnerLogin";
import { GoToTop } from "../../GoToTop";
import { useSearch } from "../../context/search";
import logoDark from "../../assets/redesign/images/logo.png";
import logoWhite from "../../assets/redesign/images/logo-white.png";
import logoModal from "../../assets/logo-hub-new-2.png";

interface IPropHeader {
  visibleFilter?: boolean;
  search?: string;
  setSearch?: (e: string) => void;
  scroll: ScrollProps;
  handleSearch?: () => void;
  show: boolean;
  setShow: (val: boolean) => void;
}

export interface ScrollProps {
  y: number;
  lastY: number;
}

const TRANSPARENT_TOP_PREFIXES = ["/sobre", "/empreendimento", "/imovel"];

function isTransparentTopPage(pathname: string) {
  if (
    pathname.includes("/imovel/") ||
    pathname.includes("/empreendimento/") ||
    pathname === "/sobre" ||
    pathname.startsWith("/sobre/")
  ) {
    return true;
  }
  return TRANSPARENT_TOP_PREFIXES.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

function isPropertyDetailPage(pathname: string) {
  return pathname.includes("/imovel/") || pathname.includes("/empreendimento/");
}

export function Header({ scroll, show, setShow }: IPropHeader) {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024,
  );
  const [modalLogin, setModalLogin] = useState(false);
  const [modalLogout, setModalLogout] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [viewPassword, setViewPassword] = useState(false);

  const { user, signOut, loading, person, signInWithEmailAndPassword } =
    useAuth();
  const { resetFilters, clearSearchResults } = useSearch();

  const transparentAtTop = isTransparentTopPage(location.pathname);
  const propertyPage = isPropertyDetailPage(location.pathname);
  const isScrolled = scroll.y > 0;

  // Transparent at top on Sobre/Imóvel/Empreendimento; on scroll → surface (Sobre) or primary (imóvel/empreendimento)
  let variant: "default" | "transparent" | "primary" | "surface" = "surface";
  if (transparentAtTop && !isScrolled) {
    variant = "transparent";
  } else if (propertyPage) {
    variant = "primary";
  } else {
    variant = "surface";
  }

  const lightChrome = variant === "transparent" || variant === "primary";

  const needsSpacer = !transparentAtTop;

  const handleLoginWithEmailAndPassword = useCallback(async () => {
    await signInWithEmailAndPassword({ email, password });
  }, [email, password, signInWithEmailAndPassword]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (user) setModalLogin(false);
  }, [user]);

  useEffect(() => {
    if (scroll.y > 48 && scroll.lastY < scroll.y) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [scroll, setShow]);

  useEffect(() => {
    setMenuOpen(false);
    setShow(false);
  }, [location.pathname, setShow]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen || isDesktop) return;
    const handler = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen, isDesktop]);

  const goHome = () => {
    resetFilters();
    clearSearchResults();
    setMenuOpen(false);
    navigate("/");
  };

  const goSobre = () => {
    setMenuOpen(false);
    navigate("/sobre");
  };

  const goProfile = () => {
    setMenuOpen(false);
    navigate("/perfil-interno");
  };

  return (
    <>
      <Container $hidden={show} $variant={variant} $needsSpacer={needsSpacer}>
        <Nav>
          <LogoButton type="button" onClick={goHome} aria-label="Ir para Home">
            <img src={lightChrome ? logoWhite : logoDark} alt="Hub Nogueira" />
          </LogoButton>

          <MenuWrap ref={menuRef}>
            <MenuToggle
              type="button"
              $light={lightChrome}
              aria-label="Abrir menu"
              aria-expanded={menuOpen}
              onClick={() => {
                if (!isDesktop) setMenuOpen((v) => !v);
              }}
            >
              <FiMenu />
            </MenuToggle>

            <DesktopDropdown className="desktop-dropdown">
              <div className="dropdown-panel">
                <MenuItem type="button" onClick={goHome}>
                  <FiHome />
                  Início
                </MenuItem>
                <MenuItem type="button" onClick={goSobre}>
                  <FiInfo />
                  Sobre Nós
                </MenuItem>
                <MenuDivider />
                {user ? (
                  <>
                    <MenuUserBlock>
                      {person?.foto_perfil ? (
                        <img src={person.foto_perfil} alt="" />
                      ) : (
                        <FaUserCircle className="avatar" size={40} />
                      )}
                      <div className="meta">
                        <span className="role">Olá,</span>
                        <span className="name">
                          {person?.nome ? person.nome.split(" ")[0] : "Olá"}
                        </span>
                      </div>
                    </MenuUserBlock>
                    <MenuItem type="button" onClick={goProfile}>
                      <FiUser />
                      Meu perfil
                    </MenuItem>
                    <MenuItem
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setModalLogout(true);
                      }}
                    >
                      <FiLogOut />
                      Logout
                    </MenuItem>
                  </>
                ) : (
                  <MenuItem
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setModalLogin(true);
                    }}
                  >
                    <FiLogIn />
                    Login
                  </MenuItem>
                )}
              </div>
            </DesktopDropdown>
          </MenuWrap>
        </Nav>

        <Modal
          title="LOGIN"
          centered
          width={380}
          open={modalLogin}
          onOk={() => setModalLogin(false)}
          onCancel={() => setModalLogin(false)}
          footer={null}
        >
          <ContentModalGoogle>
            <img src={logoModal} alt="Logo Hub Nogueira" />
            {loading && <LoadSpinnerLogin />}
            {!loading && (
              <>
                <div className="form-group">
                  <label htmlFor="header-login-email">E-mail:</label>
                  <input
                    id="header-login-email"
                    className="input-login"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    placeholder="e-mail"
                    autoComplete="on"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="header-login-password">Senha:</label>
                  <div className="input-login-div">
                    <input
                      id="header-login-password"
                      className="input-div-login"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      type={viewPassword ? "text" : "password"}
                      placeholder="senha"
                    />
                    <button
                      type="button"
                      className="button-eye"
                      onClick={() => setViewPassword(!viewPassword)}
                    >
                      {viewPassword ? <FaRegEye /> : <FaRegEyeSlash />}
                    </button>
                  </div>
                </div>

                <br />

                <button
                  type="button"
                  onClick={handleLoginWithEmailAndPassword}
                  className="button-login"
                >
                  Entrar
                </button>

                <div
                  style={{
                    marginTop: -2,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    <a
                      style={{ fontSize: "12px" }}
                      href="/esqueci-minha-senha"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Esqueci minha senha
                    </a>
                  </span>
                  <span>
                    <a
                      style={{ fontSize: "12px" }}
                      href="/primeiro-acesso"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Sou novo aqui
                    </a>
                  </span>
                </div>
              </>
            )}
          </ContentModalGoogle>
        </Modal>

        <Modal
          centered
          width={300}
          open={modalLogout}
          onOk={() => setModalLogout(false)}
          onCancel={() => setModalLogout(false)}
          footer={null}
        >
          <ContentModalGoogle>
            <img src={logoModal} alt="Logo Hub Nogueira" />
            {loading && <LoadSpinnerLogin />}
            {!loading && (
              <>
                <p className="title-logout">Tem certeza que deseja sair?</p>
                <div className="content-button">
                  <button
                    type="button"
                    onClick={() => {
                      signOut();
                      setModalLogout(false);
                    }}
                  >
                    Sair
                  </button>
                </div>
              </>
            )}
          </ContentModalGoogle>
        </Modal>
        <GoToTop />
      </Container>

      <MobileOverlay
        $open={menuOpen && !isDesktop}
        onClick={() => setMenuOpen(false)}
      />
      <MobileDrawer $open={menuOpen && !isDesktop}>
        <DrawerHeader>
          <h3>Menu</h3>
          <button
            type="button"
            aria-label="Fechar menu"
            onClick={() => setMenuOpen(false)}
          >
            <FiX />
          </button>
        </DrawerHeader>

        {user && (
          <UserRow>
            {person?.foto_perfil ? (
              <img src={person.foto_perfil} alt="" />
            ) : (
              <FaUserCircle className="avatar" size={40} />
            )}
            <div className="meta">
              <span className="role">Olá, </span>
              <span className="name">
                {person?.nome ? person.nome.split(" ")[0] : "Olá"}
              </span>
            </div>
          </UserRow>
        )}

        <MenuItem type="button" onClick={goHome}>
          <FiHome />
          Início
        </MenuItem>
        <MenuItem type="button" onClick={goSobre}>
          <FiInfo />
          Sobre Nós
        </MenuItem>
        <MenuDivider />
        {user ? (
          <>
            <MenuItem type="button" onClick={goProfile}>
              <FiUser />
              Meu perfil
            </MenuItem>
            <MenuItem
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setModalLogout(true);
              }}
            >
              <FiLogOut />
              Logout
            </MenuItem>
          </>
        ) : (
          <MenuItem
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setModalLogin(true);
            }}
          >
            <FiLogIn />
            Login
          </MenuItem>
        )}
      </MobileDrawer>

      {needsSpacer && <Spacer aria-hidden="true" />}
    </>
  );
}
